import React from 'react';
import styled from 'styled-components';
import Card from '../../Card';
import GlassPicklist, { type GlassPicklistOption } from '../../ui/GlassPicklist';

type SortMode = 'name-asc' | 'name-desc' | 'open-desc' | 'open-asc';
type SortVariant = 'stacked' | 'icon';

export type TeamFilterItem = {
  id: string;
  label: string;
  openCount: number;
  avatarKey?: string;
  avatarUrl?: string | null;
};

type TeamFilterMenuOption = {
  id: string;
  label: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
};

type TeamFilterInlineAction = {
  id: string;
  label: string;
  disabled?: boolean;
  ariaLabel?: string;
  title?: string;
  onClick: () => void;
};

type Props = {
  title: string;
  items: TeamFilterItem[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  allowDeselect?: boolean;
  includeAllRow?: {
    id: string;
    label: string;
    openCount: number;
  };
  searchPlaceholder?: string;
  sortLabel?: string;
  panelHeight?: string;
  sortVariant?: SortVariant;
  contextLabel?: string;
  metaMode?: 'inline' | 'stacked';
  getItemInlineActions?: (item: TeamFilterItem) => ReadonlyArray<TeamFilterInlineAction>;
  getItemMenuOptions?: (item: TeamFilterItem) => ReadonlyArray<TeamFilterMenuOption>;
  isItemExpandable?: (item: TeamFilterItem) => boolean;
  expandedItemId?: string | null;
  onExpandedItemChange?: (id: string | null) => void;
  renderExpandedContent?: (item: TeamFilterItem) => React.ReactNode;
  actionButton?: {
    icon?: string;
    ariaLabel: string;
    title?: string;
    disabled?: boolean;
    onClick: () => void;
  };
};

const Wrap = styled.aside`
  width: 100%;
`;

const Panel = styled(Card)<{ $panelHeight?: string }>`
  padding: 12px;
  display: grid;
  grid-template-rows: auto auto 1fr;
  gap: 10px;
  height: ${({ $panelHeight }) => $panelHeight || 'clamp(360px, 60vh, 640px)'};
  overflow: hidden;
`;

const Header = styled.div`
  display: grid;
  gap: 2px;
`;

const HeaderTitle = styled.div`
  font-weight: 900;
  letter-spacing: .3px;
  color: #E8EAED;
  text-transform: uppercase;
  font-size: 12px;
`;

const HeaderContext = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .1px;
  text-transform: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Controls = styled.div`
  display: grid;
  gap: 8px;
`;

const SearchRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(255, 255, 255, .04);
  color: #E8EAED;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 700;
  &::placeholder { color: #9aa4b2; }
  &:focus {
    outline: none;
    border-color: rgba(224, 161, 94, .7);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .15);
  }
`;

const SortMenuWrap = styled.div`
  position: relative;
`;

const IconButtons = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const SortIconButton = styled.button<{ $open: boolean }>`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid ${({ $open }) => ($open ? 'rgba(224, 161, 94, .65)' : 'rgba(255, 255, 255, .14)')};
  background: rgba(255, 255, 255, .04);
  color: #e8eaed;
  display: grid;
  place-items: center;
  cursor: pointer;
  font-size: 16px;
  font-weight: 900;
  line-height: 1;

  &:hover {
    border-color: rgba(224, 161, 94, .55);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .75);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }
`;

const ActionIconButton = styled.button<{ $disabled?: boolean }>`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(255, 255, 255, .04);
  color: #e8eaed;
  display: grid;
  place-items: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-size: 19px;
  font-weight: 900;
  line-height: 1;
  opacity: ${({ $disabled }) => ($disabled ? .45 : 1)};

  &:hover {
    border-color: ${({ $disabled }) => ($disabled ? 'rgba(255, 255, 255, .14)' : 'rgba(224, 161, 94, .55)')};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .75);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }
`;

const SortMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 8;
  width: 220px;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(16, 20, 28, .94);
  box-shadow: 0 14px 28px rgba(0, 0, 0, .35);
`;

const SortSelect = styled.select`
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(255, 255, 255, .04);
  color: #E8EAED;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .2px;
  option { background: #12161b; color: #E8EAED; }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  min-height: 0;
`;

const ItemBlock = styled.div`
  display: block;
  position: relative;
  flex: 0 0 auto;
`;

const Row = styled.div<{ $active?: boolean; $color?: string }>`
  display: grid;
  grid-template-columns: 32px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, .06);
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, .14)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#E8EAED' : '#cbd2e1')};
  &:hover { background: rgba(255, 255, 255, .07); }
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .7);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .15);
  }

  .avatar {
    width: 32px; height: 32px; border-radius: 8px;
    background: ${({ $color }) => $color || '#E5E7EB'};
    color: #111827; display: grid; place-items: center; font-weight: 900;
    font-size: 12px;
    overflow: hidden;
  }
  .avatarImage {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .name {
    font-weight: 800;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .main {
    min-width: 0;
    display: grid;
    gap: 2px;
  }
  .right {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .meta {
    font-size: 12px;
    font-weight: 800;
    opacity: .9;
  }
`;

const ExpandIndicator = styled.span<{ $expanded?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  color: ${({ $expanded }) => ($expanded ? '#E8EAED' : '#9aa4b2')};
  font-size: 13px;
  font-weight: 900;
  line-height: 1;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(0deg)' : 'rotate(-90deg)')};
  transition: transform .16s ease, color .16s ease;
`;

const ExpandedSurface = styled.div`
  margin-top: 6px;
  margin-left: 52px;
  width: auto;
  padding: 6px 0 10px;
  display: grid;
  gap: 10px;
  background: transparent;
  border: 0;
  min-width: 0;
  max-height: min(40vh, 320px);
  overflow-y: auto;
  align-content: start;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, .14);
    border-radius: 999px;
  }
`;

const ItemMenuWrap = styled.div`
  position: relative;
`;

const ItemInlineActionButton = styled.button<{ $disabled?: boolean }>`
  min-height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, .16);
  background: transparent;
  color: #E8EAED;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .04em;
  text-transform: uppercase;
  opacity: ${({ $disabled }) => ($disabled ? .45 : 1)};

  &:hover {
    border-color: ${({ $disabled }) =>
      $disabled ? 'rgba(255, 255, 255, .16)' : 'rgba(224, 161, 94, .5)'};
    background: ${({ $disabled }) =>
      $disabled ? 'transparent' : 'rgba(255, 255, 255, .04)'};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .7);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }
`;

const ItemMenuButton = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(255, 255, 255, .04);
  color: #E8EAED;
  display: grid;
  place-items: center;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;

  &:hover {
    border-color: rgba(224, 161, 94, .55);
  }
`;

const ItemMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 12;
  min-width: 140px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(16, 20, 28, .96);
  box-shadow: 0 12px 28px rgba(0, 0, 0, .35);
  padding: 6px;
  display: grid;
  gap: 4px;
`;

const ItemMenuEntry = styled.button<{ $danger?: boolean }>`
  all: unset;
  cursor: pointer;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 800;
  color: ${({ $danger }) => ($danger ? '#FCA5A5' : '#E8EAED')};

  &:hover {
    background: rgba(255, 255, 255, .08);
  }

  &[disabled] {
    opacity: .45;
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  color: #9aa4b2;
  font-size: 12px;
  font-weight: 700;
  padding: 8px 2px;
`;

const palette = ['#FCA5A5', '#A7F3D0', '#93C5FD', '#FDE68A', '#E9D5FF', '#FBCFE8', '#FDBA74', '#34D399', '#F472B6', '#60A5FA'];
function colorFor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}
function initials(value: string) {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'NA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function FilterAvatar({
  label,
  avatarUrl,
}: {
  label: string;
  avatarUrl?: string | null;
}) {
  const [imageFailed, setImageFailed] = React.useState(false);
  const hasImage = Boolean(avatarUrl && !imageFailed);

  React.useEffect(() => {
    setImageFailed(false);
  }, [avatarUrl]);

  if (hasImage) {
    return (
      <img
        className="avatarImage"
        src={avatarUrl || undefined}
        alt=""
        aria-hidden="true"
        onError={() => setImageFailed(true)}
      />
    );
  }

  return <>{initials(label)}</>;
}

const SORT_OPTIONS: ReadonlyArray<GlassPicklistOption<SortMode>> = [
  { value: 'open-desc', label: 'Most open' },
  { value: 'open-asc', label: 'Least open' },
  { value: 'name-asc', label: 'Name A-Z' },
  { value: 'name-desc', label: 'Name Z-A' },
];

export default function TeamFilterPanel({
  title,
  items,
  selectedId,
  onSelect,
  allowDeselect = true,
  includeAllRow,
  searchPlaceholder = 'Search...',
  sortLabel = 'Sort',
  panelHeight,
  sortVariant = 'stacked',
  contextLabel,
  metaMode = 'inline',
  getItemInlineActions,
  getItemMenuOptions,
  isItemExpandable,
  expandedItemId,
  onExpandedItemChange,
  renderExpandedContent,
  actionButton,
}: Props) {
  const [query, setQuery] = React.useState('');
  const [sort, setSort] = React.useState<SortMode>('open-desc');
  const [sortMenuOpen, setSortMenuOpen] = React.useState(false);
  const [openItemMenuId, setOpenItemMenuId] = React.useState<string | null>(null);
  const menuHostRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setSortMenuOpen(false);
  }, [sort]);

  React.useEffect(() => {
    if (!openItemMenuId) return;
    const onWindowMouseDown = (event: MouseEvent) => {
      if (!menuHostRef.current) return;
      if (menuHostRef.current.contains(event.target as Node)) return;
      setOpenItemMenuId(null);
    };
    window.addEventListener('mousedown', onWindowMouseDown);
    return () => window.removeEventListener('mousedown', onWindowMouseDown);
  }, [openItemMenuId]);

  const processed = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = items.filter((item) => item.label.toLowerCase().includes(q));
    const list = [...filtered];
    if (sort === 'name-asc') list.sort((a, b) => a.label.localeCompare(b.label));
    if (sort === 'name-desc') list.sort((a, b) => b.label.localeCompare(a.label));
    if (sort === 'open-desc') list.sort((a, b) => b.openCount - a.openCount || a.label.localeCompare(b.label));
    if (sort === 'open-asc') list.sort((a, b) => a.openCount - b.openCount || a.label.localeCompare(b.label));
    return list;
  }, [items, query, sort]);

  const supportsExpansion = Boolean(renderExpandedContent && onExpandedItemChange);

  const handleRowActivate = React.useCallback((itemId: string) => {
    const nextSelectedId =
      selectedId === itemId
        ? (allowDeselect ? null : itemId)
        : itemId;
    onSelect(nextSelectedId);
    if (!supportsExpansion || !onExpandedItemChange) return;
    onExpandedItemChange(expandedItemId === itemId ? null : itemId);
  }, [
    allowDeselect,
    expandedItemId,
    onExpandedItemChange,
    onSelect,
    selectedId,
    supportsExpansion,
  ]);

  return (
    <Wrap>
      <Panel $panelHeight={panelHeight}>
        <Header>
          <HeaderTitle>{title}</HeaderTitle>
          {contextLabel ? <HeaderContext>{contextLabel}</HeaderContext> : null}
        </Header>
        <Controls>
          {sortVariant === 'icon' ? (
            <SearchRow>
              <SearchInput
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={`${title} search`}
              />
              <IconButtons>
                <SortMenuWrap>
                  <SortIconButton
                    type="button"
                    onClick={() => setSortMenuOpen((prev) => !prev)}
                    $open={sortMenuOpen}
                    aria-label={`${sortLabel} options`}
                    title={`${sortLabel}: ${SORT_OPTIONS.find((option) => option.value === sort)?.label || ''}`}
                  >
                    {'\u21C5'}
                  </SortIconButton>
                  {sortMenuOpen ? (
                    <SortMenu>
                      <GlassPicklist<SortMode>
                        value={sort}
                        options={SORT_OPTIONS}
                        onChange={setSort}
                        ariaLabel={`${title} ${sortLabel}`}
                      />
                    </SortMenu>
                  ) : null}
                </SortMenuWrap>
                {actionButton ? (
                  <ActionIconButton
                    type="button"
                    onClick={actionButton.onClick}
                    aria-label={actionButton.ariaLabel}
                    title={actionButton.title}
                    disabled={actionButton.disabled}
                    $disabled={actionButton.disabled}
                  >
                    {actionButton.icon || '+'}
                  </ActionIconButton>
                ) : null}
              </IconButtons>
            </SearchRow>
          ) : (
            <>
              <SearchInput
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={`${title} search`}
              />
              <SortSelect
                value={sort}
                onChange={(event) => setSort(event.target.value as SortMode)}
                aria-label={`${title} ${sortLabel}`}
              >
                <option value="open-desc">{sortLabel}: Most open</option>
                <option value="open-asc">{sortLabel}: Least open</option>
                <option value="name-asc">{sortLabel}: Name A-Z</option>
                <option value="name-desc">{sortLabel}: Name Z-A</option>
              </SortSelect>
            </>
          )}
        </Controls>

        <List>
          {includeAllRow ? (
            <Row
              onClick={() => onSelect(selectedId === includeAllRow.id ? (allowDeselect ? null : includeAllRow.id) : includeAllRow.id)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onSelect(
                  selectedId === includeAllRow.id
                    ? (allowDeselect ? null : includeAllRow.id)
                    : includeAllRow.id,
                );
              }}
              $active={selectedId === includeAllRow.id}
              aria-label={includeAllRow.label}
              role="button"
              tabIndex={0}
            >
              <div className="avatar" style={{ background: '#E5E7EB' }}>ALL</div>
              <div className="main">
                <div className="name">{includeAllRow.label}</div>
                {metaMode === 'stacked' ? <div className="meta">{includeAllRow.openCount} open</div> : null}
              </div>
              <div className="right">
                {metaMode === 'inline' ? <div className="meta">{includeAllRow.openCount} open</div> : null}
              </div>
            </Row>
          ) : null}

          {processed.length ? (
            processed.map((item) => {
              const inlineActions = getItemInlineActions?.(item) ?? [];
              const menuOptions = getItemMenuOptions?.(item) ?? [];
              const hasMenu = menuOptions.length > 0;
              const canExpand = supportsExpansion && (isItemExpandable ? isItemExpandable(item) : true);
              const isExpanded = expandedItemId === item.id;
              return (
                <ItemBlock key={item.id}>
                  <Row
                    onClick={() => handleRowActivate(item.id)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      handleRowActivate(item.id);
                    }}
                    $active={selectedId === item.id}
                    $color={colorFor(item.avatarKey || item.id)}
                    aria-label={item.label}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="avatar">
                      <FilterAvatar label={item.label} avatarUrl={item.avatarUrl} />
                    </div>
                    <div className="main">
                      <div className="name">{item.label}</div>
                      {metaMode === 'stacked' ? <div className="meta">{item.openCount} open</div> : null}
                    </div>
                    <div className="right">
                      {metaMode === 'inline' ? <div className="meta">{item.openCount} open</div> : null}
                      {canExpand ? <ExpandIndicator $expanded={isExpanded}>{'\u2304'}</ExpandIndicator> : null}
                      {inlineActions.map((action) => (
                        <ItemInlineActionButton
                          key={action.id}
                          type="button"
                          aria-label={action.ariaLabel || action.label}
                          title={action.title || action.label}
                          disabled={action.disabled}
                          $disabled={action.disabled}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (action.disabled) return;
                            action.onClick();
                          }}
                        >
                          {action.label}
                        </ItemInlineActionButton>
                      ))}
                      {hasMenu ? (
                        <ItemMenuWrap
                          ref={openItemMenuId === item.id ? menuHostRef : null}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <ItemMenuButton
                            type="button"
                            aria-label={`${item.label} options`}
                            title={`${item.label} options`}
                            onClick={() => setOpenItemMenuId((prev) => (prev === item.id ? null : item.id))}
                          >
                            {'\u22EE'}
                          </ItemMenuButton>
                          {openItemMenuId === item.id ? (
                            <ItemMenu>
                              {menuOptions.map((option) => (
                                <ItemMenuEntry
                                  key={option.id}
                                  type="button"
                                  $danger={option.danger}
                                  disabled={option.disabled}
                                  onClick={() => {
                                    if (option.disabled) return;
                                    setOpenItemMenuId(null);
                                    option.onSelect();
                                  }}
                                >
                                  {option.label}
                                </ItemMenuEntry>
                              ))}
                            </ItemMenu>
                          ) : null}
                        </ItemMenuWrap>
                      ) : null}
                    </div>
                  </Row>
                  {canExpand && isExpanded ? (
                    <ExpandedSurface>
                      {renderExpandedContent?.(item)}
                    </ExpandedSurface>
                  ) : null}
                </ItemBlock>
              );
            })
          ) : (
            <Empty>No matches.</Empty>
          )}
        </List>
      </Panel>
    </Wrap>
  );
}



